import { FC, useEffect } from 'react';
import { ButtonLinkRight } from '@zoopla-eng/zds-components/button';
import {
    ExternalLinkSmall,
    ExternalLinkMedium,
} from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { MojoLogo } from '@zoopla/assets';
import { EventNames, trackSegmentAndGAEvent } from '@zoopla/tracking';
import {
    contentStyle,
    headingStyle,
    copyStyle,
    mojoLogoStyle,
    partnershipStyle,
    wrapperStyle,
    centredContent,
} from './MojoBanner.styles.css';

const MOJO_URL = (variant: MojoBannerVariant) => {
    const campaign =
        variant === 'home_values'
            ? 'InstantValHighIntentButton1'
            : variant === 'my_home'
            ? 'MyHomev2'
            : 'ValBoosterBannerTest';

    const content =
        variant === 'interruption'
            ? '&utm_content=form'
            : variant === 'success'
            ? '&utm_content=form_success'
            : '';

    return `https://zoopla.mojomortgages.com/get-started?utm_source=partner&utm_medium=zoopla&utm_campaign=${campaign}${content}`;
};

const VARIANT_CONTENT = {
    interruption: {
        heading: 'Find a new mortgage deal that is right for you',
        copy: 'Let Mojo’s advisers find the best deal you can get by comparing over 90 lenders and 1000s of products.',
        ctaCategory: 'secondary' as const,
    },
    success: {
        heading: 'Are you going to need a new mortgage deal soon?',
        copy: 'Find a new mortgage deal that is right for you and your home’s value. Let Mojo’s advisers find the best deal you can get by comparing over 90 lenders and 1000s of products.',
        ctaCategory: 'primary' as const,
    },
    my_home: {
        heading: 'Are you going to need a new mortgage deal soon?',
        copy: `Find a new mortgage deal that is right for you and your home’s value. Let Mojo’s advisers find the best deal you can get by comparing over 90 lenders & 1000s of products.`,
        ctaCategory: 'primary' as const,
    },
    home_values: {
        heading: 'Find the best remortgage deals based on your LTV',
        copy: '',
        ctaCategory: 'primary' as const,
    },
};

export type MojoBannerVariant = keyof typeof VARIANT_CONTENT;
export type MojoBannerTrackingValues = {
    page: string;
    eventCategory: string;
    eventLabel: string;
};

type MojoBannerProps = {
    variant: MojoBannerVariant;
    trackingValues: MojoBannerTrackingValues;
};

export const MojoBanner: FC<MojoBannerProps> = ({
    variant,
    trackingValues: { page, eventCategory, eventLabel },
}) => {
    const { heading, copy, ctaCategory } = VARIANT_CONTENT[variant];

    useEffect(() => {
        trackSegmentAndGAEvent(
            EventNames.MODULE_SHOWN,
            {
                eventCategory,
                eventAction: 'module_shown',
                eventLabel: eventLabel,
            },
            page
        );
    }, [variant, eventCategory, page, eventLabel]);

    const onCtaClick = () => {
        trackSegmentAndGAEvent(
            EventNames.MOJO_OUTBOUND_CLICK,
            {
                eventCategory: eventCategory,
                eventAction: 'click',
                eventLabel: 'get_mortgage',
            },
            page
        );
    };

    const headingFontGroup = {
        my_home: fontGroups.kettle,
        home_values: fontGroups.whisk,
        interruption: fontGroups.mug,
        success: fontGroups.mug,
    };

    return (
        <div className={wrapperStyle({ variant })}>
            <div className={contentStyle}>
                <h2 className={`${headingFontGroup[variant]} ${headingStyle}`}>
                    {heading}
                </h2>
                {copy && (
                    <p className={`${fontGroups.ladle} ${copyStyle}`}>{copy}</p>
                )}
            </div>
            {ctaCategory && (
                <ButtonLinkRight
                    href={MOJO_URL(variant)}
                    category={ctaCategory}
                    Icon={
                        variant === 'home_values'
                            ? ExternalLinkSmall
                            : ExternalLinkMedium
                    }
                    rel="noreferrer"
                    target="_blank"
                    onClick={onCtaClick}
                >
                    Get expert advice
                </ButtonLinkRight>
            )}

            <p
                className={`${fontGroups.fork} ${partnershipStyle} ${
                    variant === 'home_values' && centredContent
                }`}
            >
                In partnership with
                <MojoLogo className={mojoLogoStyle} />
            </p>
        </div>
    );
};
